import { createSlice, createSelector, createEntityAdapter } from "@reduxjs/toolkit";
import { simpleSetPayload } from "../constants";
import { sortByFieldAlphabetically, sortByName } from "@amplio-common/funcs/commonFunctionalities";
import { currentUser } from "@app/routes/auth/selectors";
import { ROLES } from "@app/constants";
import { SLICE_NAMES } from "./sliceNames";

//define root selector

const websocketSlice_STATE_ROOT_SELECTOR = state => state[SLICE_NAMES.WEBSOCKET_SLICE];
const rootSelector = s => createSelector(websocketSlice_STATE_ROOT_SELECTOR, s);

const createSimpleSelector = (fieldName) => createSelector(
  [state => state[SLICE_NAMES.WEBSOCKET_SLICE][fieldName]], field => field,
);


const sortFunc = sortByFieldAlphabetically("name");

export const wsStudentsAdapter = createEntityAdapter({
  sortComparer: sortFunc,
});

export const wsMapTSToStudentStatus = (ts) => ({
  id: ts.student.id,
  name: ts.student.name,
  firstName: ts.student.first_name,
  wsConnected: false,
  lastSeenDate: null,
});

const wsStudentsAdapterSelectors = wsStudentsAdapter.getSelectors(websocketSlice_STATE_ROOT_SELECTOR);
  


const initialState = {
  isLoadingWS: true,
  UNSAFE_websocket: null,
  WSError: null,
  studentsLastSeenDates: {},
  lastSeenDate: null,
  teacherWSConnected: false,
  ...wsStudentsAdapter.getInitialState(),
  lastPong: Date.now(),
};


export const websocketSlice = createSlice({
  name: SLICE_NAMES.WEBSOCKET_SLICE,
  initialState,
  reducers: {
    clearWS: () => ({ ...initialState }),
    wsConnect: (state) => { state.isLoadingWS = true; },
    wsReconnect: () => {},
    wsClose: () => {},
    setWSError: simpleSetPayload("WSError"),
    setTeacherWSConnected: simpleSetPayload("teacherWSConnected"),
    setIsTeacherOnline: simpleSetPayload("isTeacherOnline"),
    updateTeacherLastSeen: state => { state.lastSeenDate = new Date().getTime(); },
    wsConnected: (state, action) => {
      state.isLoadingWS = false;
      state.UNSAFE_websocket = action.payload.websocket;
    },
    wsUpdateStudentStatus: wsStudentsAdapter.updateOne,
    wsSetStudentsStatus: wsStudentsAdapter.setAll,
    wsUpdateAllStudents: wsStudentsAdapter.updateMany,
    setLastPong: simpleSetPayload("lastPong"),
    setPongInterval: () => {},
  },
  extraReducers: {

  },
});

export default websocketSlice.reducer;

//actions
export const { wsUpdateStudentStatus, wsSetStudentsStatus, wsUpdateAllStudents, clearWS,
  wsConnect, wsReconnect, wsConnected, setWSError, wsClose, updateTeacherLastSeen, setTeacherWSConnected, setIsTeacherOnline, setPongInterval, setLastPong,
} = websocketSlice.actions;

//selectors
const UNSAFE_selectGetWebsocket = createSimpleSelector("UNSAFE_websocket");
const selectWSError = createSimpleSelector("WSError");
const selectLastSeenDate = createSimpleSelector("lastSeenDate");
const selectTeacherWSConnected = createSimpleSelector("teacherWSConnected");
const selectIsTeacherOnline = createSimpleSelector("isTeacherOnline");

const selectConnectedStudentsIds = connnected => rootSelector(t => Object.values(t.entities).filter(
  s => connnected ? s.wsConnected : !s.wsConnected,
).sort(sortByName).map(s => s.id));
const selectConnectedStudents = connnected => rootSelector(t => Object.values(t.entities).filter(
  s => connnected ? s.wsConnected : !s.wsConnected,
).sort(sortByName));

export const { selectAll: wsSelectAllStudentsStatus, selectIds: wsSelectStudentStatusIds,
  selectById: wsSelectStudentStatusById, selectEntities: selectAllStudentStatusDict } = wsStudentsAdapterSelectors;
export { UNSAFE_selectGetWebsocket, selectWSError, selectLastSeenDate, selectTeacherWSConnected, selectIsTeacherOnline,
  selectConnectedStudentsIds, selectConnectedStudents };

export const selectStudentFirstNameById = createSelector(
  [wsSelectStudentStatusById], studentStatus => studentStatus?.firstName,
);

export const selectStudentIndexById = sId => createSelector(
  [wsSelectStudentStatusIds], (studentStatusIdsArr) => studentStatusIdsArr.findIndex(s => s === sId) + 1,
);

export const selectNumConnectedStudents = createSelector(
  [wsStudentsAdapterSelectors.selectEntities], students => Object.values(students).filter(item => item.wsConnected).length,
);

export const selectIsOtherParticipantsConnected = createSelector(
  [currentUser, selectNumConnectedStudents, selectTeacherWSConnected],
  (currUser, numConnectedStudents, teacherWSConnected) => currUser.role === ROLES.THERAPIST.id ? numConnectedStudents >= 1 : teacherWSConnected,
);

export const selectLastPong = createSimpleSelector("lastPong");
